/* eslint-disable react/prop-types */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useAdvanceTableContext } from '../../../providers/AdvanceTableProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, FormControl, InputGroup } from 'react-bootstrap';

const DEBOUNCE_MS = 300;

const readGlobalFilter = (table) => {
	if (!table) return '';
	if (typeof table.getState === 'function') {
		return table.getState()?.globalFilter ?? '';
	}
	return table.globalFilter ?? '';
};

/**
 * @param {string} [placeholder]
 * @param {string} [className]
 * @param {'default'|'clear-only'|'none'} [trailing] 右侧：默认搜索/清空；仅有值时清空；不显示
 * @param {'search'|'text'} [type]
 */
const AdvanceTableSearchBox = ({ placeholder = '搜索...', className, trailing = 'default', type = 'search' }) => {
	const table = useAdvanceTableContext();
	const globalFilter = readGlobalFilter(table);
	const setGlobalFilter = table?.setGlobalFilter;
	const [value, setValue] = useState(globalFilter ?? '');
	const debounceRef = useRef(null);

	useEffect(() => {
		setValue(globalFilter ?? '');
	}, [globalFilter]);

	const applyFilter = useCallback(
		(v) => {
			if (typeof setGlobalFilter === 'function') {
				setGlobalFilter(v && v.trim() ? v.trim() : undefined);
			}
		},
		[setGlobalFilter],
	);

	useEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			applyFilter(value);
		}, DEBOUNCE_MS);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [value, applyFilter]);

	const handleClear = useCallback(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		setValue('');
		applyFilter('');
	}, [applyFilter]);

	const handleKeyDown = useCallback(
		(event) => {
			if (event.key !== 'Enter') return;
			event.preventDefault();
			if (debounceRef.current) clearTimeout(debounceRef.current);
			applyFilter(value);
		},
		[applyFilter, value],
	);

	const hasValue = Boolean(value && value.trim());
	const showClear = trailing !== 'none' && hasValue;
	const showSearchAffix = trailing === 'default' && !hasValue;

	return (
		<InputGroup className={classNames(className, 'position-relative')}>
			<FormControl
				value={value || ''}
				onChange={({ target: { value: v } }) => setValue(v)}
				onKeyDown={handleKeyDown}
				size='sm'
				id='search'
				placeholder={placeholder}
				type={type}
				inputMode='search'
				enterKeyHint='search'
				className='shadow-none'
				aria-label={placeholder}
				autoComplete='off'
			/>
			{showClear ? (
				<Button size='sm' variant='outline-secondary' className='border-300 hover-border-secondary' onClick={handleClear} title='清空搜索'>
					<FontAwesomeIcon icon='times' className='fs-10' />
				</Button>
			) : null}
			{showSearchAffix ? (
				<Button size='sm' variant='outline-secondary' className='border-300 hover-border-secondary' disabled>
					<FontAwesomeIcon icon='search' className='fs-10' />
				</Button>
			) : null}
		</InputGroup>
	);
};

export default AdvanceTableSearchBox;
